/**
 * Generates a mailto link for Forward trade bank negotiation emails.
 * Supports three subtypes: regular Forward, Forward Asiático, and Forward Starting.
 */

import { TradeBookingForm, SettlementType } from '../types/tradeBooking';
import { formatAmountDisplay, formatPriceDisplay } from './amountFormatter';

type ForwardSubtype = 'regular' | 'asiatico' | 'starting';

const formatDateDD_MM_YYYY = (dateStr: string): string => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
};

const formatDateFromDate = (date: Date | null): string => {
  if (!date) return '';
  const d = String(date.getDate()).padStart(2, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const y = date.getFullYear();
  return `${d}/${m}/${y}`;
};

/** Shared labels that are the same across all forward subtypes */
interface SharedLabels {
  greeting: string;
  entityLabel: string;
  typeLabel: string;
  directionLabel: string;
  directionBuy: string;
  directionSell: string;
  amountLabel: string;
  tradeDateLabel: string;
  valueDateLabel: string;
  maturityLabel: string;
  fixingRefLabel: string;
  settlementTypeLabel: string;
  settlementCurrLabel: string;
  regards: string;
  // Settlement type values
  settlementCompensacion: string;
  settlementEntregaFisica: string;
}

/** Subtype-specific labels */
interface SubtypeLabels {
  subject: string;
  intro: string;
  typeValue: string;
  closing: string;
  // Asiático
  fixingDatesLabel?: string;
  // Forward Starting
  spotFixingLabel?: string;
  priceToBeFixed?: string;
  // Forward Starting — averaged entry
  typeValueAveraged?: string;
  avgDatesLabel?: string;
  fwdPointsLabel?: string;
  priceToBeFixedAveraged?: string;
  // Regular Forward
  priceLabel?: string;
  spotLabel?: string;
}

interface MailtoTranslations {
  shared: SharedLabels;
  regular: SubtypeLabels;
  asiatico: SubtypeLabels;
  starting: SubtypeLabels;
}

const translations: Record<string, MailtoTranslations> = {
  es: {
    shared: {
      greeting: 'Estimados,',
      entityLabel: 'Entidad',
      typeLabel: 'Tipo de operación',
      directionLabel: 'Dirección',
      directionBuy: 'Compra',
      directionSell: 'Venta',
      amountLabel: 'Monto',
      tradeDateLabel: 'Fecha de Operación',
      valueDateLabel: 'Fecha Valor',
      maturityLabel: 'Vencimiento',
      fixingRefLabel: 'Referencia de Fijación',
      settlementTypeLabel: 'Tipo de Liquidación',
      settlementCurrLabel: 'Moneda de Liquidación',
      regards: 'Saludos,',
      settlementCompensacion: 'Compensación',
      settlementEntregaFisica: 'Entrega Física',
    },
    regular: {
      subject: 'Forward — {{pair}} {{direction}} {{amount}} — Solicitud de Cotización',
      intro: 'Nos gustaría cotizar el siguiente Forward:',
      typeValue: 'Forward',
      closing: 'Por favor, indíquenos el precio para esta operación.',
      priceLabel: 'Precio',
      spotLabel: 'Spot',
    },
    asiatico: {
      subject: 'Forward Asiático — {{pair}} {{direction}} {{amount}} — Solicitud de Cotización',
      intro: 'Nos gustaría cotizar el siguiente Forward Asiático:',
      typeValue: 'Forward Asiático',
      closing: 'Por favor, indíquenos el precio para esta operación.',
      fixingDatesLabel: 'Fechas de Fijación',
    },
    starting: {
      subject: 'Forward Starting — {{pair}} {{direction}} {{amount}} — Solicitud de Cotización',
      intro: 'Nos gustaría cotizar el siguiente Forward Starting:',
      typeValue: 'Forward Starting',
      closing: 'Por favor, indíquenos los puntos forward para esta operación.',
      spotFixingLabel: 'Fixing de Spot',
      priceLabel: 'Precio',
      priceToBeFixed: 'Por Fijar (se determina en fecha valor)',
      typeValueAveraged: 'Forward Starting (Spot Promedio)',
      avgDatesLabel: 'Fechas de Promedio Spot',
      fwdPointsLabel: 'Puntos Forward',
      priceToBeFixedAveraged: 'Por Fijar (promedio spot + puntos forward)',
    },
  },
  en: {
    shared: {
      greeting: 'Dear Sir/Madam,',
      entityLabel: 'Entity',
      typeLabel: 'Trade type',
      directionLabel: 'Direction',
      directionBuy: 'Buy',
      directionSell: 'Sell',
      amountLabel: 'Amount',
      tradeDateLabel: 'Trade Date',
      valueDateLabel: 'Value Date',
      maturityLabel: 'Maturity',
      fixingRefLabel: 'Fixing Reference',
      settlementTypeLabel: 'Settlement Type',
      settlementCurrLabel: 'Settlement Currency',
      regards: 'Kind regards,',
      settlementCompensacion: 'Cash Settlement',
      settlementEntregaFisica: 'Physical Delivery',
    },
    regular: {
      subject: 'Forward — {{pair}} {{direction}} {{amount}} — Quote Request',
      intro: 'We would like to request a quote for the following Forward:',
      typeValue: 'Forward',
      closing: 'Please advise the forward rate for this trade.',
      priceLabel: 'Price',
      spotLabel: 'Spot',
    },
    asiatico: {
      subject: 'Average Rate Forward — {{pair}} {{direction}} {{amount}} — Quote Request',
      intro: 'We would like to request a quote for the following Average Rate Forward:',
      typeValue: 'Average Rate Forward',
      closing: 'Please advise the forward rate for this trade.',
      fixingDatesLabel: 'Fixing Dates',
    },
    starting: {
      subject: 'Forward Starting — {{pair}} {{direction}} {{amount}} — Quote Request',
      intro: 'We would like to request a quote for the following Forward Starting trade:',
      typeValue: 'Forward Starting',
      closing: 'Please advise the forward points for this trade.',
      spotFixingLabel: 'Spot Fixing',
      priceLabel: 'Price',
      priceToBeFixed: 'To Be Fixed (determined on value date)',
      typeValueAveraged: 'Forward Starting (Averaged Spot)',
      avgDatesLabel: 'Spot Averaging Dates',
      fwdPointsLabel: 'Forward Points',
      priceToBeFixedAveraged: 'To Be Fixed (averaged spot + forward points)',
    },
  },
  pt: {
    shared: {
      greeting: 'Prezados,',
      entityLabel: 'Entidade',
      typeLabel: 'Tipo de operação',
      directionLabel: 'Direção',
      directionBuy: 'Compra',
      directionSell: 'Venda',
      amountLabel: 'Montante',
      tradeDateLabel: 'Data de Operação',
      valueDateLabel: 'Data Valor',
      maturityLabel: 'Vencimento',
      fixingRefLabel: 'Referência de Fixing',
      settlementTypeLabel: 'Tipo de Liquidação',
      settlementCurrLabel: 'Moeda de Liquidação',
      regards: 'Cumprimentos,',
      settlementCompensacion: 'Compensação',
      settlementEntregaFisica: 'Entrega Física',
    },
    regular: {
      subject: 'Forward — {{pair}} {{direction}} {{amount}} — Solicitação de Cotação',
      intro: 'Gostaríamos de cotar o seguinte Forward:',
      typeValue: 'Forward',
      closing: 'Por favor, indiquem o preço para esta operação.',
      priceLabel: 'Preço',
      spotLabel: 'Spot',
    },
    asiatico: {
      subject: 'Average Rate Forward — {{pair}} {{direction}} {{amount}} — Solicitação de Cotação',
      intro: 'Gostaríamos de cotar o seguinte Average Rate Forward:',
      typeValue: 'Average Rate Forward',
      closing: 'Por favor, indiquem o preço para esta operação.',
      fixingDatesLabel: 'Datas de Fixing',
    },
    starting: {
      subject: 'Forward Starting — {{pair}} {{direction}} {{amount}} — Solicitação de Cotação',
      intro: 'Gostaríamos de cotar o seguinte Forward Starting:',
      typeValue: 'Forward Starting',
      closing: 'Por favor, indiquem os pontos forward para esta operação.',
      spotFixingLabel: 'Fixing de Spot',
      priceLabel: 'Preço',
      priceToBeFixed: 'A Fixar (determinado na data valor)',
      typeValueAveraged: 'Forward Starting (Spot Médio)',
      avgDatesLabel: 'Datas de Média Spot',
      fwdPointsLabel: 'Pontos Forward',
      priceToBeFixedAveraged: 'A Fixar (média spot + pontos forward)',
    },
  },
};

const fixingReferenceDisplay: Record<string, string> = {
  'USD_OBS': 'USD Obs',
  'UF': 'UF',
  'BLOOMBERG_11AM': 'Bloomberg 11 AM',
  'N/A': 'N/A',
};

function getSubtype(form: TradeBookingForm): ForwardSubtype {
  if (form.isAverageRateForward) return 'asiatico';
  if (form.isForwardStarting) return 'starting';
  return 'regular';
}

export function generateForwardMailto(
  form: TradeBookingForm,
  userName: string,
  entityName: string,
  locale: string
): string {
  const t = translations[locale] || translations['es'];
  const subtype = getSubtype(form);
  const shared = t.shared;
  const sub = t[subtype];

  const pair = `${form.currency1}/${form.currency2}`;
  const isBuy = form.direction === 'BUY';
  const directionShort = isBuy ? shared.directionBuy : shared.directionSell;
  const oppositeShort = isBuy ? shared.directionSell : shared.directionBuy;
  const directionFull = `${directionShort} ${form.currency1} / ${oppositeShort} ${form.currency2}`;
  const formattedAmount = formatAmountDisplay(form.amount, locale, 2);
  const tradeDate = formatDateFromDate(form.tradeDate);
  const valueDate = formatDateFromDate(form.valueDate);
  const maturity = formatDateFromDate(form.expiryDate);
  const fixingRef = fixingReferenceDisplay[form.fixingReference] || form.fixingReference;
  const settlementValue = form.settlementType === SettlementType.ENTREGA_FISICA
    ? shared.settlementEntregaFisica
    : shared.settlementCompensacion;
  const settlementCurr = form.settlementCurrency || form.currency2 || '';

  // Build subject
  const subject = sub.subject
    .replace('{{pair}}', pair)
    .replace('{{direction}}', directionShort)
    .replace('{{amount}}', `${formattedAmount} ${form.currency1}`);

  // Build body — shared header
  const lines: string[] = [
    shared.greeting,
    '',
    sub.intro,
    '',
    `${shared.entityLabel}: ${entityName}`,
    `${shared.typeLabel}: ${subtype === 'starting' && form.forwardStartingFixingSchedule?.length > 1 ? sub.typeValueAveraged : sub.typeValue}`,
    `${shared.directionLabel}: ${directionFull}`,
    `${shared.amountLabel}: ${formattedAmount} ${form.currency1}`,
    `${shared.tradeDateLabel}: ${tradeDate}`,
    `${shared.valueDateLabel}: ${valueDate}`,
    ...(subtype === 'starting' ? [`${sub.spotFixingLabel}: ${fixingRef}`] : []),
    `${shared.maturityLabel}: ${maturity}`,
    `${shared.fixingRefLabel}: ${fixingRef}`,
    `${shared.settlementTypeLabel}: ${settlementValue}`,
  ];

  // Settlement currency only for Compensación
  if (form.settlementType === SettlementType.COMPENSACION) {
    lines.push(`${shared.settlementCurrLabel}: ${settlementCurr}`);
  }

  // Subtype-specific content
  if (subtype === 'asiatico') {
    const sortedDates = [...form.averageRateFixingSchedule]
      .filter(e => e.date)
      .sort((a, b) => a.date.localeCompare(b.date));
    lines.push('');
    lines.push(`${sub.fixingDatesLabel}:`);
    lines.push(...sortedDates.map((entry, i) => `  ${i + 1}. ${formatDateDD_MM_YYYY(entry.date)}`));
  } else if (subtype === 'starting') {
    const isAveraged = form.forwardStartingFixingSchedule && form.forwardStartingFixingSchedule.length > 1;
    if (isAveraged) {
      // Include averaging dates and forward points
      const sortedDates = [...form.forwardStartingFixingSchedule]
        .filter(e => e.date)
        .sort((a, b) => a.date.localeCompare(b.date));
      if (form.forwardPoints !== null) {
        lines.push(`${sub.fwdPointsLabel}: ${formatPriceDisplay(form.forwardPoints, locale, 4)}`);
      }
      lines.push('');
      lines.push(`${sub.avgDatesLabel}:`);
      lines.push(...sortedDates.map((entry, i) => `  ${i + 1}. ${formatDateDD_MM_YYYY(entry.date)}`));
      lines.push('');
      lines.push(`${sub.priceLabel}: ${sub.priceToBeFixedAveraged}`);
    } else {
      lines.push(`${sub.priceLabel}: ${sub.priceToBeFixed}`);
    }
  } else {
    // Regular forward — include price and spot if available
    if (form.price !== null && form.price > 0) {
      lines.push(`${sub.priceLabel}: ${formatPriceDisplay(form.price, locale, 4)}`);
    }
    if (form.spotPrice !== null && form.spotPrice > 0) {
      lines.push(`${sub.spotLabel}: ${formatPriceDisplay(form.spotPrice, locale, 4)}`);
    }
    if (form.forwardPoints !== null) {
      const fwdPtsLabel = locale === 'es' ? 'Puntos Forward' : locale === 'pt' ? 'Pontos Forward' : 'Forward Points';
      lines.push(`${fwdPtsLabel}: ${formatPriceDisplay(form.forwardPoints, locale, 4)}`);
    }
  }

  // Closing
  lines.push('');
  lines.push(sub.closing);
  lines.push('');
  lines.push(shared.regards);
  lines.push(userName);
  lines.push(entityName);

  const body = lines.join('\n');

  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

// Keep backward-compatible export for any other callers
export const generateForwardAsiaticMailto = generateForwardMailto;

// --- Multi-trade email (HTML table copied to clipboard + mailto shell) ---

export interface MultiTradeMailtoRow {
  tradeType: string;
  ourParty: string;
  direction: string;
  amount: number | null;
  currency1: string;
  currency2: string;
  spotPrice: number | null;
  forwardPoints: number | null;
  price: number | null;
  contraAmount: number | null;
  tradeDate: string;
  maturityDate: string;
  settlementType: string;
  settlementCurrency: string;
  fixingReference: string;
  isForwardStarting: boolean;
  selected: boolean;
}

const multiTradeTranslations: Record<string, {
  subject: string;
  greeting: string;
  intro: string;
  closing: string;
  regards: string;
  headers: {
    ourParty: string; direction: string;
    amount: string; pair: string; spot: string; fwdPts: string; price: string;
    contraAmount: string; tradeDate: string; maturity: string;
    settlement: string; settlCcy: string; fixingRef: string;
  };
  directionBuy: string; directionSell: string;
  settlementComp: string; settlementPhys: string;
  priceToBeFixed: string;
}> = {
  es: {
    subject: 'Solicitud de Cotización — {{summary}}',
    greeting: 'Estimados,',
    intro: 'Nos gustaría solicitar cotización para las siguientes operaciones:',
    closing: 'Por favor, indíquennos los precios para estas operaciones.',
    regards: 'Saludos,',
    headers: {
      ourParty: 'Entidad', direction: 'Dirección', amount: 'Monto', pair: 'Par',
      spot: 'Spot', fwdPts: 'Ptos Fwd', price: 'Precio',
      contraAmount: 'Monto Contra', tradeDate: 'Fecha Op.',
      maturity: 'Vencimiento', settlement: 'Liquidación',
      settlCcy: 'Mon. Liq.', fixingRef: 'Ref. Fixing',
    },
    directionBuy: 'Compra', directionSell: 'Venta',
    settlementComp: 'Compensación', settlementPhys: 'Entrega Física',
    priceToBeFixed: 'Por Fijar',
  },
  en: {
    subject: 'Quote Request — {{summary}}',
    greeting: 'Dear Sir/Madam,',
    intro: 'We would like to request a quote for the following trades:',
    closing: 'Please advise the rates for these trades.',
    regards: 'Kind regards,',
    headers: {
      ourParty: 'Entity', direction: 'Direction', amount: 'Amount', pair: 'Pair',
      spot: 'Spot', fwdPts: 'Fwd Pts', price: 'Price',
      contraAmount: 'Contra Amt', tradeDate: 'Trade Date',
      maturity: 'Maturity', settlement: 'Settlement',
      settlCcy: 'Settl Ccy', fixingRef: 'Fixing Ref',
    },
    directionBuy: 'Buy', directionSell: 'Sell',
    settlementComp: 'Cash Settlement', settlementPhys: 'Physical Delivery',
    priceToBeFixed: 'TBF',
  },
  pt: {
    subject: 'Solicitação de Cotação — {{summary}}',
    greeting: 'Prezados,',
    intro: 'Gostaríamos de solicitar cotação para as seguintes operações:',
    closing: 'Por favor, indiquem os preços para estas operações.',
    regards: 'Cumprimentos,',
    headers: {
      ourParty: 'Entidade', direction: 'Direção', amount: 'Montante', pair: 'Par',
      spot: 'Spot', fwdPts: 'Ptos Fwd', price: 'Preço',
      contraAmount: 'Montante Contra', tradeDate: 'Data Op.',
      maturity: 'Vencimento', settlement: 'Liquidação',
      settlCcy: 'Moeda Liq.', fixingRef: 'Ref. Fixing',
    },
    directionBuy: 'Compra', directionSell: 'Venda',
    settlementComp: 'Compensação', settlementPhys: 'Entrega Física',
    priceToBeFixed: 'A Fixar',
  },
};

const fmtNum = (v: number | null, decimals = 0): string => {
  if (v == null) return '';
  return Number(v).toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

/** Build a trade-type summary like "3 Forwards + 2 Spots" or "5 Forward Starting" */
function buildTradeSummary(trades: MultiTradeMailtoRow[]): string {
  const selected = trades.filter(r => r.selected);
  const counts: Record<string, number> = {};
  for (const r of selected) {
    let label = r.tradeType || 'Forward';
    if (r.isForwardStarting) label = 'Forward Starting';
    else if (label === 'FORWARD') label = 'Forward';
    else if (label === 'SPOT') label = 'Spot';
    counts[label] = (counts[label] || 0) + 1;
  }
  const parts = Object.entries(counts).map(([type, n]) => `${n} ${type}${n > 1 ? 's' : ''}`);
  return parts.join(' + ');
}

/** Returns the mailto URL with subject + greeting shell */
export function getMultiTradeMailtoUrl(trades: MultiTradeMailtoRow[], locale: string, bcc?: string[]): string {
  const t = multiTradeTranslations[locale] || multiTradeTranslations['es'];
  const selected = trades.filter(r => r.selected);
  if (selected.length === 0) return '';
  const summary = buildTradeSummary(trades);
  const subject = t.subject.replace('{{summary}}', summary);
  const body = [t.greeting, '', t.intro, '', '', t.closing, '', t.regards].join('\n');
  let url = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  if (bcc && bcc.length > 0) {
    url += `&bcc=${bcc.join(';')}`;
  }
  return url;
}

/** Returns an HTML table string for pasting into an email */
export function generateMultiTradeTableHtml(trades: MultiTradeMailtoRow[], locale: string): string {
  const t = multiTradeTranslations[locale] || multiTradeTranslations['es'];
  const h = t.headers;
  const selected = trades.filter(r => r.selected);
  if (selected.length === 0) return '';

  // Determine optional columns
  const hasContra = selected.some(r => r.contraAmount != null);
  const hasSettlCcy = selected.some(r => r.settlementType === 'COMPENSACION');
  const hasFixingRef = selected.some(r => r.settlementType === 'COMPENSACION');

  // Column definitions
  type Col = { header: string; getValue: (r: MultiTradeMailtoRow, i: number) => string; align?: 'right' };
  const cols: Col[] = [
    { header: '#', getValue: (_r, i) => String(i + 1) },
    { header: h.ourParty, getValue: r => r.ourParty },
    { header: h.direction, getValue: r => r.direction === 'BUY' ? t.directionBuy : t.directionSell },
    { header: h.amount, getValue: r => fmtNum(r.amount), align: 'right' },
    { header: h.pair, getValue: r => `${r.currency1}/${r.currency2}` },
    { header: h.spot, getValue: r => r.isForwardStarting
        ? (fixingReferenceDisplay[r.fixingReference] || r.fixingReference)
        : fmtNum(r.spotPrice, 2), align: 'right' },
    { header: h.fwdPts, getValue: r => fmtNum(r.forwardPoints, 2), align: 'right' },
    { header: h.price, getValue: r => r.isForwardStarting ? t.priceToBeFixed : fmtNum(r.price, 2), align: 'right' },
  ];
  if (hasContra) {
    cols.push({ header: h.contraAmount, getValue: r => r.isForwardStarting ? t.priceToBeFixed : fmtNum(r.contraAmount), align: 'right' });
  }
  cols.push({ header: h.tradeDate, getValue: r => formatDateDD_MM_YYYY(r.tradeDate) });
  cols.push({ header: h.maturity, getValue: r => formatDateDD_MM_YYYY(r.maturityDate) });
  cols.push({ header: h.settlement, getValue: r => r.settlementType === 'ENTREGA_FISICA' ? t.settlementPhys : t.settlementComp });
  if (hasSettlCcy) {
    cols.push({ header: h.settlCcy, getValue: r => r.settlementCurrency || '' });
  }
  if (hasFixingRef) {
    cols.push({ header: h.fixingRef, getValue: r => r.settlementType === 'COMPENSACION'
        ? (fixingReferenceDisplay[r.fixingReference] || r.fixingReference) : '' });
  }

  // Inline styles for email compatibility
  const tableStyle = 'border-collapse:collapse;font-family:Arial,sans-serif;font-size:13px;';
  const thStyle = 'border:1px solid #ccc;padding:6px 10px;background:#f0f0f0;font-weight:600;text-align:left;white-space:nowrap;';
  const tdStyle = 'border:1px solid #ccc;padding:6px 10px;white-space:nowrap;';
  const tdRightStyle = tdStyle + 'text-align:right;';

  const headerRow = cols.map(c =>
    `<th style="${thStyle}">${c.header}</th>`
  ).join('');

  const dataRows = selected.map((r, i) =>
    '<tr>' + cols.map(c => {
      const style = c.align === 'right' ? tdRightStyle : tdStyle;
      return `<td style="${style}">${c.getValue(r, i)}</td>`;
    }).join('') + '</tr>'
  ).join('');

  return `<table style="${tableStyle}"><thead><tr>${headerRow}</tr></thead><tbody>${dataRows}</tbody></table>`;
}
